import { useEffect, useState } from "react";
import api, { formatApiError } from "../lib/api";
import { Loader2, Radio, ScanLine, Ticket, QrCode } from "lucide-react";

const fmtGNF = (n) => new Intl.NumberFormat("fr-FR").format(n || 0);

/** Statistiques d'entrée en direct — polling 5s. Utilisé par l'admin et l'artiste. */
export default function LiveEntryStats({ eventId }) {
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    let active = true;
    const fetchStats = () => {
      api.get(`/events/${eventId}/live-stats`)
        .then((r) => { if (active) { setData(r.data); setError(null); } })
        .catch((e) => { if (active) setError(formatApiError(e)); });
    };
    fetchStats();
    const timer = setInterval(fetchStats, 5000);
    return () => { active = false; clearInterval(timer); };
  }, [eventId]);

  if (error) return <div className="card-mc p-8 text-center text-destructive text-sm" data-testid="live-stats-error">{error}</div>;
  if (!data) return <div className="py-12 text-center"><Loader2 className="w-6 h-6 mx-auto animate-spin" /></div>;

  const { totals, types, breakdown, recent_scans } = data;

  return (
    <div data-testid="live-stats-panel">
      <div className="flex items-center gap-2 mb-4">
        <span className="relative flex h-2.5 w-2.5">
          <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-[hsl(var(--accent))] opacity-75" />
          <span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-[hsl(var(--accent))]" />
        </span>
        <p className="text-[11px] font-mono uppercase tracking-wider text-muted-foreground">En direct · actualisation toutes les 5 s</p>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-0 card-mc mb-5" data-testid="live-totals">
        {[
          { label: "Billets émis", value: totals.sold, Icon: Ticket },
          { label: "Entrées scannées", value: totals.scanned, Icon: ScanLine, accent: true },
          { label: "Restant à entrer", value: totals.remaining, Icon: QrCode },
          { label: "Taux d'entrée", value: `${totals.entry_rate_percent}%`, Icon: Radio, accent: true },
        ].map((c, i) => (
          <div key={i} className="p-4 border-r border-b border-border last:border-r-0">
            <p className="text-[10px] font-mono uppercase tracking-wider text-muted-foreground flex items-center gap-1"><c.Icon className="w-3 h-3" /> {c.label}</p>
            <p className={`mt-1 font-display font-black text-3xl tracking-tighter ${c.accent ? "text-[hsl(var(--accent))]" : ""}`}>{c.value}</p>
          </div>
        ))}
      </div>

      <div className="card-mc p-4 mb-5">
        <div className="flex justify-between text-[11px] font-mono uppercase tracking-wider text-muted-foreground mb-1.5">
          <span>Entrées : {totals.scanned} / {totals.sold} billets émis</span>
          <span>Remplissage salle : {totals.fill_rate_percent}% de {totals.capacity} places</span>
        </div>
        <div className="h-3 bg-muted rounded-full overflow-hidden" data-testid="entry-progress-bar">
          <div className="h-full bg-[hsl(var(--accent))] transition-all duration-700" style={{ width: `${totals.entry_rate_percent}%` }} />
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
        <div className="card-mc p-4" data-testid="live-types">
          <p className="overline mb-3">Par type de billet</p>
          {types.map((t, i) => (
            <div key={i} className="mb-3 last:mb-0">
              <div className="flex justify-between text-xs mb-1">
                <span className="font-bold">{t.name}</span>
                <span className="font-mono">{t.scanned}/{t.sold} scannés · {fmtGNF(t.quantity_total)} places</span>
              </div>
              <div className="h-2 bg-muted rounded-full overflow-hidden">
                <div className="h-full bg-[hsl(var(--primary))] transition-all duration-700" style={{ width: t.sold ? `${(t.scanned / t.sold) * 100}%` : "0%" }} />
              </div>
            </div>
          ))}
          <div className="border-t border-border mt-4 pt-3 flex gap-4 text-xs font-mono" data-testid="live-breakdown">
            <span>🎫 En ligne : {breakdown.online.scanned}/{breakdown.online.sold}</span>
            <span>🖨️ Bracelets : {breakdown.bracelet.scanned}/{breakdown.bracelet.sold}</span>
          </div>
        </div>

        <div className="card-mc p-4" data-testid="live-recent-scans">
          <p className="overline mb-3">Derniers passages</p>
          {recent_scans.length === 0 ? (
            <p className="text-sm text-muted-foreground py-4 text-center">Aucun scan pour le moment.</p>
          ) : (
            <ul className="space-y-2">
              {recent_scans.map((s, i) => (
                <li key={i} className="flex items-center justify-between text-xs border-b border-border/60 pb-1.5 last:border-0">
                  <div>
                    <span className="font-mono font-bold">{s.code || "—"}</span>
                    <span className="text-muted-foreground ml-2">{s.ticket_type_name}{s.source === "bracelet" ? " · bracelet" : ""}</span>
                  </div>
                  <span className="font-mono text-muted-foreground">{s.scanned_at ? new Date(s.scanned_at).toLocaleTimeString("fr-FR") : ""}</span>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>
    </div>
  );
}
